import React, { useState } from 'react';
import { Check, ArrowRight, Zap, Users, TrendingUp, Shield, Clock, Sparkles } from 'lucide-react';
// CheckCircle component matching the Feature Comparison style
const CheckCircle = (props: React.SVGProps<SVGSVGElement>) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" focusable="false" fill="currentColor" {...props} data-id="element-1560">
    <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm45.66,85.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z" data-id="element-1561" />
  </svg>;
interface PricingFeature {
  text: string;
  icon?: 'check' | 'zap' | 'users' | 'trending' | 'shield' | 'clock' | 'sparkles';
}
interface PricingPlan {
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: PricingFeature[];
  ctaText: string;
  ctaLink: string;
  recommended?: boolean;
  variant?: 'light' | 'dark';
}
interface PricingSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  plans?: PricingPlan[];
  'data-id'?: string;
}
const defaultPlans: PricingPlan[] = [{
  name: 'Core',
  description: 'For investors closing 1-3 deals per month',
  monthlyPrice: 497,
  yearlyPrice: 398,
  features: [{
    text: 'Unlimited markets',
    icon: 'trending'
  }, {
    text: '1,500 automated offers per month',
    icon: 'zap'
  }, {
    text: 'AI-powered property analysis',
    icon: 'sparkles'
  }, {
    text: 'Response tracking',
    icon: 'check'
  }, {
    text: 'Deal pipeline analytics',
    icon: 'trending'
  }, {
    text: 'Custom offer templates',
    icon: 'check'
  }, {
    text: 'Email support',
    icon: 'shield'
  }],
  ctaText: 'Get Started — $97',
  ctaLink: '/trial',
  recommended: false,
  variant: 'light'
}, {
  name: 'Pro',
  description: 'For investors closing 3-5 deals per month',
  monthlyPrice: 997,
  yearlyPrice: 798,
  features: [{
    text: 'Unlimited markets',
    icon: 'trending'
  }, {
    text: '4,500 automated offers per month',
    icon: 'zap'
  }, {
    text: 'AI-powered property analysis',
    icon: 'sparkles'
  }, {
    text: 'Smart response tracking & follow-up',
    icon: 'check'
  }, {
    text: 'Deal pipeline analytics',
    icon: 'trending'
  }, {
    text: 'Multi-market campaigns',
    icon: 'users'
  }, {
    text: 'Custom offer templates',
    icon: 'check'
  }, {
    text: 'Email & phone support',
    icon: 'shield'
  }],
  ctaText: 'Get Started — $97',
  ctaLink: '/trial',
  recommended: false,
  variant: 'light'
}, {
  name: 'Scale',
  description: 'For investors closing 10+ deals per month',
  monthlyPrice: 1597,
  yearlyPrice: 1278,
  features: [{
    text: 'Unlimited markets',
    icon: 'trending'
  }, {
    text: 'Unlimited automated offers',
    icon: 'zap'
  }, {
    text: 'Custom AI training',
    icon: 'sparkles'
  }, {
    text: 'White-labeled offer system',
    icon: 'shield'
  }, {
    text: 'API & CRM integrations',
    icon: 'users'
  }, {
    text: 'Dedicated account manager',
    icon: 'users'
  }, {
    text: '24/7 priority support',
    icon: 'clock'
  }, {
    text: 'Custom onboarding & setup',
    icon: 'check'
  }],
  ctaText: 'Get Started — $97',
  ctaLink: '/trial',
  recommended: true,
  variant: 'dark'
}];
const FeatureIcon = ({
  icon
}: {
  icon?: string;
}) => {
  const iconClass = 'w-5 h-5 text-[#3ab795]';
  const strokeWidth = 2;
  switch (icon) {
    case 'zap':
      return <Zap className={iconClass} strokeWidth={strokeWidth} data-id="element-1562" />;
    case 'users':
      return <Users className={iconClass} strokeWidth={strokeWidth} data-id="element-1563" />;
    case 'trending':
      return <TrendingUp className={iconClass} strokeWidth={strokeWidth} data-id="element-1564" />;
    case 'shield':
      return <Shield className={iconClass} strokeWidth={strokeWidth} data-id="element-1565" />;
    case 'clock':
      return <Clock className={iconClass} strokeWidth={strokeWidth} data-id="element-1566" />;
    case 'sparkles':
      return <Sparkles className={iconClass} strokeWidth={strokeWidth} data-id="element-1567" />;
    default:
      return <CheckCircle className={iconClass} data-id="element-1568" />;
  }
};
export const PricingSection: React.FC<PricingSectionProps> = ({
  title = 'Pricing & Plans',
  subtitle = 'Scale Your Deal Flow With Real Leverage',
  description = 'One accepted offer covers your subscription. Most investors close within 60 days.',
  plans = defaultPlans,
  'data-id': dataId
}) => {
  const [isYearly, setIsYearly] = useState(false);
  return <section className="relative w-full min-h-screen bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212] py-24 md:py-32 px-6 md:px-8 flex items-center justify-center overflow-hidden" data-id="element-1569">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 opacity-30" data-id="element-1570">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-10" data-id="element-1571" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-10" data-id="element-1572" />
      </div>

      <div className="relative max-w-[1280px] w-full flex flex-col items-center gap-16 z-10" data-id="element-1573">
        {/* Header */}
        <div className="flex flex-col items-center gap-8 max-w-4xl" data-id="element-1574">
          {/* Pill Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm" data-id="element-1575">
            <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-1576" />
            <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-1577">
              {title}
            </p>
          </div>
          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-center leading-tight tracking-tight" data-id="element-1578">
            <span className="text-white" data-id="element-1579">Scale Your Deal Flow </span>
            <span className="relative inline-block" data-id="element-1580">
              <span className="text-[#3ab795]" data-id="element-1581">With Real Leverage</span>
              <span className="absolute bottom-2 left-0 w-full h-4 bg-[#3ab795]/20 -z-10" data-id="element-1582"></span>
            </span>
          </h1>
          {/* Description */}
          <p className="text-lg md:text-xl text-[#b8bcc8] text-center leading-relaxed max-w-3xl" data-id="element-1583">
            {description}
          </p>
        </div>
        {/* Billing Toggle with Savings */}
        <div className="flex flex-col items-center gap-3" data-id="element-1584">
          <p className="text-sm font-medium text-[#3ab795]" data-id="element-1585">
            💰 Save 20% with yearly billing
          </p>
          <div className="flex items-center gap-4" data-id="element-1586">
            <span className="text-base font-medium text-[#b8bcc8]" data-id="element-1587">
              Monthly
            </span>
            <button onClick={() => setIsYearly(!isYearly)} className="relative w-16 h-8 bg-white/10 rounded-full p-1.5 transition-all hover:bg-white/20 active:scale-95 shadow-sm" aria-label="Toggle billing period" data-id="element-1588">
              <div className={`w-6 h-6 bg-[#3ab795] rounded-full transition-all duration-300 shadow-md ${isYearly ? 'translate-x-8' : 'translate-x-0'}`} data-id="element-1589" />
            </button>
            <span className="text-base font-medium text-[#b8bcc8]" data-id="element-1590">Yearly</span>
          </div>
        </div>
        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 w-full items-center" data-id="element-1591">
          {plans.map((plan, index) => <PricingCard key={index} plan={plan} isYearly={isYearly} data-id="element-1592" />)}
        </div>
        {/* Bottom Line */}
        <div className="flex flex-col items-center gap-2 text-center max-w-2xl" data-id="element-1593">
          <div className="flex items-center gap-3" data-id="element-1594">
            <span className="text-2xl" data-id="element-1595">⚡</span>
            <p className="text-lg md:text-xl font-semibold text-white" data-id="element-1596">
              $97 onboarding fee includes a 1-on-1 setup call and is credited
              toward your first month.
            </p>
          </div>
          <p className="text-sm text-[#b8bcc8]" data-id="element-1597">
            30-day money-back guarantee on all plans.
          </p>
        </div>
      </div>
    </section>;
};
interface PricingCardProps {
  plan: PricingPlan;
  isYearly: boolean;
}
const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  isYearly
}) => {
  const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const isDark = plan.variant === 'dark';
  const isScale = plan.name === 'Scale';
  return <div className={`relative ${isScale ? 'lg:scale-105' : ''}`} data-id="element-1598">
      {/* More Prominent White Glow Effect */}
      <div className="absolute inset-0 rounded-[32px] blur-3xl bg-white opacity-30 -z-10" style={{
      transform: 'scale(0.92)'
    }} data-id="element-1599" />
      {/* Badge - "Best Value" for Scale - Improved positioning */}
      {plan.recommended && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10" data-id="element-1600">
          <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#3ab795] to-[#2d9575] text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg uppercase tracking-wide" data-id="element-1601">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-1602" />
            <span data-id="element-1603">Best Value</span>
          </span>
        </div>}
      <div className={`rounded-[32px] p-6 md:p-8 flex flex-col gap-6 transition-all duration-300 hover:scale-[1.02] border border-white/10 ${isDark ? 'bg-[#1a1a1a]' : 'bg-[#1a1a1a]'} shadow-[0_8px_24px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.4)]`} data-id="element-1604">
        {/* Header - Aligned Top Padding */}
        <div className="flex flex-col gap-3 pt-2" data-id="element-1605">
          <h3 className="text-2xl md:text-3xl font-bold text-white" data-id="element-1606">
            {plan.name}
          </h3>
          <p className="text-base leading-relaxed text-[#b8bcc8]" data-id="element-1607">
            {plan.description}
          </p>
        </div>
        {/* Divider */}
        <div className="h-px bg-white/10" data-id="element-1608" />
        {/* Price */}
        <div className="flex flex-col gap-3" data-id="element-1609">
          <div className="flex items-center gap-2" data-id="element-1610">
            <div className="flex items-start gap-1" data-id="element-1611">
              <span className="text-4xl md:text-5xl font-bold text-white" data-id="element-1612">
                $
              </span>
              <span className="text-4xl md:text-5xl font-bold text-white" data-id="element-1613">
                {price.toLocaleString()}
              </span>
            </div>
            <span className="text-base font-medium text-[#b8bcc8]" data-id="element-1614">
              per month
            </span>
          </div>
          {/* Show "billed yearly" when yearly is selected */}
          {isYearly && <p className="text-sm text-[#b8bcc8]" data-id="element-1615">
              Billed yearly at ${(price * 12).toLocaleString()}
            </p>}
        </div>
        {/* Divider */}
        <div className="h-px bg-white/10" data-id="element-1616" />
        {/* Features */}
        <div className="flex flex-col gap-3" data-id="element-1617">
          {plan.features.map((feature, index) => <div key={index} className="flex items-start gap-3 group" data-id="element-1618">
              <div className="w-5 h-5 flex-shrink-0 mt-0.5 transition-transform group-hover:scale-110" data-id="element-1619">
                <FeatureIcon icon={feature.icon} data-id="element-1620" />
              </div>
              <p className="text-base leading-relaxed text-[#b8bcc8]" data-id="element-1621">
                {feature.text}
              </p>
            </div>)}
        </div>
        {/* Divider */}
        <div className="h-px bg-white/10" data-id="element-1622" />
        {/* CTA Button - Conditional styling based on plan */}
        <div className="flex flex-col gap-3" data-id="element-1623">
          <a href={plan.ctaLink} className={`flex items-center justify-center gap-3 px-6 py-5 rounded-xl font-semibold text-base transition-all duration-300 hover:scale-105 active:scale-95 group ${isScale ? 'bg-[#3ab795] text-white shadow-[0_4px_16px_rgba(58,183,149,0.3)] hover:shadow-[0_8px_24px_rgba(58,183,149,0.4)]' : 'bg-white text-[#1D1D1D] shadow-[0_4px_16px_rgba(255,255,255,0.1)] hover:bg-[#3ab795] hover:text-white hover:shadow-[0_8px_24px_rgba(58,183,149,0.3)]'}`} data-id="element-1624">
            <span data-id="element-1625">{plan.ctaText}</span>
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2} data-id="element-1626" />
          </a>
          {/* 30-Day Money-Back Guarantee Badge */}
          <div className="inline-flex items-center justify-center gap-2 px-3 py-1.5 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-lg mx-auto" data-id="element-1627">
            <Shield className="w-4 h-4 text-[#3ab795]" strokeWidth={2} data-id="element-1628" />
            <span className="text-xs font-medium text-[#3ab795]" data-id="element-1629">
              30-Day Money-Back Guarantee
            </span>
          </div>
        </div>
      </div>
    </div>;
};